// frontend/src/components/ui/PWAInstallBanner.tsx
import React, { useState, useEffect } from 'react';
import { Smartphone, Share, X, Plus, ArrowUp } from 'lucide-react';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { useLanguage } from '../../contexts/LanguageContext';

const DISMISSED_KEY = 'pwa-install-dismissed';

/**
 * PWA "Add to Home Screen" install banner.
 *
 * Behavior:
 * - Hidden if the app is already installed (standalone mode).
 * - Hidden if the user has previously dismissed it (localStorage flag).
 * - On Chrome/Android: shows a banner with a native install button.
 * - On iOS Safari: shows a step-by-step manual guide modal.
 * - Only shown after the user has interacted with the app (not on first load).
 */
export const PWAInstallBanner: React.FC = () => {
    const { isInstallable, isInstalled, isIOSSafari, promptInstall } = useInstallPrompt();
    const { lang } = useLanguage();
    const [dismissed, setDismissed] = useState(false);
    const [showIOSGuide, setShowIOSGuide] = useState(false);
    const [delayPassed, setDelayPassed] = useState(false);

    // Small delay so banner doesn't flash on initial load
    useEffect(() => {
        const timer = setTimeout(() => setDelayPassed(true), 3000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (localStorage.getItem(DISMISSED_KEY) === 'true') {
            setDismissed(true);
        }
    }, []);

    const handleDismiss = () => {
        setDismissed(true);
        localStorage.setItem(DISMISSED_KEY, 'true');
    };

    const handleInstall = async () => {
        const outcome = await promptInstall();
        if (outcome === 'accepted') {
            setDismissed(true);
        }
    };

    const handleIOSInstall = () => {
        setShowIOSGuide(true);
    };

    // Don't show if: already installed, dismissed, or delay not passed
    if (isInstalled || dismissed || !delayPassed) return null;
    // Don't show if nothing to offer (no install prompt and not iOS)
    if (!isInstallable && !isIOSSafari) return null;

    return (
        <>
            {/* ── Main Install Banner ───────────────────────────── */}
            <div className="fixed bottom-0 left-0 right-0 z-[200] animate-in fade-in slide-in-from-bottom-4 duration-500 md:bottom-6 md:left-auto md:right-6 md:max-w-sm">
                <div className="flex items-start gap-3 p-4 bg-slate-900/95 border border-slate-700/60 text-white shadow-2xl backdrop-blur-md md:rounded-2xl">
                    {/* Icon */}
                    <div className="flex items-center justify-center w-10 h-10 bg-orange-500/20 border border-orange-500/30 rounded-xl shrink-0 mt-0.5">
                        <Smartphone className="w-5 h-5 text-orange-400" />
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white">
                            {lang === 'tr' ? 'Ana Ekrana Ekle' : 'Add to Home Screen'}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5 leading-snug">
                            {lang === 'tr'
                                ? 'Çevrimdışı hesaplama, native uygulama deneyimi'
                                : 'Offline calculation, native app experience'}
                        </p>

                        {/* CTA Buttons */}
                        <div className="flex items-center gap-2 mt-3">
                            <button
                                onClick={isIOSSafari ? handleIOSInstall : handleInstall}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-400 text-white text-xs font-semibold rounded-lg transition-colors"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                {lang === 'tr' ? 'Ekle' : 'Install'}
                            </button>
                            <button
                                onClick={handleDismiss}
                                className="px-3 py-1.5 text-slate-400 hover:text-white text-xs transition-colors"
                            >
                                {lang === 'tr' ? 'Şimdi Değil' : 'Not Now'}
                            </button>
                        </div>
                    </div>

                    {/* Dismiss X */}
                    <button
                        onClick={handleDismiss}
                        className="text-slate-500 hover:text-white transition-colors shrink-0"
                        aria-label="Dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* ── iOS Safari Manual Guide Modal ─────────────────── */}
            {showIOSGuide && (
                <div className="fixed inset-0 z-[400] flex items-end justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="w-full max-w-sm bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold text-white">
                                {lang === 'tr' ? 'Ana Ekrana Ekle' : 'Add to Home Screen'}
                            </h3>
                            <button
                                onClick={() => setShowIOSGuide(false)}
                                className="text-slate-400 hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Steps */}
                        <ol className="space-y-4">
                            <li className="flex items-start gap-3">
                                <span className="flex items-center justify-center w-7 h-7 bg-orange-500 text-white text-xs font-bold rounded-full shrink-0">1</span>
                                <div>
                                    <p className="text-sm text-white font-medium flex items-center gap-1.5">
                                        {lang === 'tr' ? 'Paylaş butonuna dokun' : 'Tap the Share button'}
                                        <Share className="w-4 h-4 text-blue-400 shrink-0" />
                                    </p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        {lang === 'tr'
                                            ? 'Safari toolbar\'ında (altta veya üstte)'
                                            : 'In the Safari toolbar (bottom or top)'}
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="flex items-center justify-center w-7 h-7 bg-orange-500 text-white text-xs font-bold rounded-full shrink-0">2</span>
                                <div>
                                    <p className="text-sm text-white font-medium flex items-center gap-1.5">
                                        {lang === 'tr' ? '"Ana Ekrana Ekle"ye dokun' : 'Tap "Add to Home Screen"'}
                                        <Plus className="w-4 h-4 text-slate-300 shrink-0" />
                                    </p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        {lang === 'tr'
                                            ? 'Menüde aşağı kaydırman gerekebilir'
                                            : 'You may need to scroll down in the menu'}
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="flex items-center justify-center w-7 h-7 bg-emerald-500 text-white text-xs font-bold rounded-full shrink-0">✓</span>
                                <div>
                                    <p className="text-sm text-white font-medium">
                                        {lang === 'tr' ? '"Ekle"ye dokun' : 'Tap "Add"'}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        {lang === 'tr'
                                            ? 'Uygulama ana ekranında hazır!'
                                            : 'App will appear on your home screen!'}
                                    </p>
                                </div>
                            </li>
                        </ol>

                        <button
                            onClick={() => { setShowIOSGuide(false); handleDismiss(); }}
                            className="w-full mt-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-xl transition-colors"
                        >
                            {lang === 'tr' ? 'Anladım' : 'Got it'}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default PWAInstallBanner;
